import { callParkingBoxApi } from "./parkingBoxApi";

export type LastUpdateInfo = {
  dateLabel: string;
  timeLabel: string;
};

const DATE_FORMATTER = new Intl.DateTimeFormat("pl-PL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const TIME_FORMATTER = new Intl.DateTimeFormat("pl-PL", {
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_REGEX =
  /(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:[T\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/;

const CANDIDATE_KEYS = [
  "last_update",
  "lastUpdate",
  "updated_at",
  "updatedAt",
  "ctime",
  "date",
  "datetime",
  "timestamp",
];

const NESTED_KEYS = ["data", "_data_", "response", "body", "result", "results", "payload"];

const parseDateValue = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const millis = value > 1e12 ? value : value * 1000;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return null;
    }
    const fastParsed = new Date(trimmed.replace(" ", "T"));
    if (!Number.isNaN(fastParsed.getTime())) {
      return fastParsed;
    }
    const match = trimmed.match(DATE_REGEX);
    if (match) {
      const [, day, month, year, hour = "0", minute = "0", second = "0"] = match;
      const parsed = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  return null;
};

const extractLastUpdateDate = (payload: unknown, depth = 0): Date | null => {
  if (depth > 4) {
    return null;
  }

  const directDate = parseDateValue(payload);
  if (directDate) {
    return directDate;
  }

  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const nested = extractLastUpdateDate(entry, depth + 1);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;

  for (const key of CANDIDATE_KEYS) {
    if (key in record) {
      const parsed = parseDateValue(record[key]);
      if (parsed) {
        return parsed;
      }
    }
  }

  for (const nestedKey of NESTED_KEYS) {
    if (nestedKey in record) {
      const nested = extractLastUpdateDate(record[nestedKey], depth + 1);
      if (nested) {
        return nested;
      }
    }
  }

  for (const value of Object.values(record)) {
    if (value && typeof value === "object") {
      const nested = extractLastUpdateDate(value, depth + 1);
      if (nested) {
        return nested;
      }
    }
  }

  return null;
};

export const normalizeLastUpdate = (payload: unknown): LastUpdateInfo | null => {
  const date = extractLastUpdateDate(payload);
  if (!date) {
    return null;
  }
  return {
    dateLabel: DATE_FORMATTER.format(date),
    timeLabel: TIME_FORMATTER.format(date),
  };
};

export const fetchLastUpdateInfo = async (options: { jwtToken?: string | null } = {}): Promise<LastUpdateInfo | null> => {
  const response = await callParkingBoxApi(
    {
      action: "get-last-update",
    },
    { jwtToken: options.jwtToken ?? undefined, useStoredToken: true },
  );
  return normalizeLastUpdate(response.json);
};
